import React from 'react';
import { Skeleton } from '@design-system/Skeleton';
import { Image } from '@design-system/Image';
import { isHexColor } from '@utils/checkHexCode';
import { ProgressBar } from '@design-system/ProgressBar';
import { AboutSection } from '@screens/LevelTwoDetail/AboutSection';
import { Trends } from '@design-system/Trends';
import { transformTrendToVGraph } from '@/utils/health/transformVGraph';
import { cn } from '@/utils/styles';
import {
  CommonlyLinkedSymptoms,
  IScoreDetailResponse,
} from '@viome/viome-base/src/usecase/Health/Health.model';
import { useGetScoreReference } from '@viome/viome-base/src/usecase/Health/HealthQuery.ts';
import { HealthReferenceSection } from '@screens/HealthPages/HealthReferenceSection';
import { getL2ImageUrl } from '@utils/nutrition/getImageUrl';

interface HeaderSectionProps {
  isLoading: boolean;
  l2Score: IScoreDetailResponse;
  commonlyLinkedSymptoms?: CommonlyLinkedSymptoms;
  scoreId: number;
  'data-testid'?: string;
}

export const L2HeaderSection: React.FC<HeaderSectionProps> = ({
  l2Score,
  isLoading,
  commonlyLinkedSymptoms,
  scoreId,
  'data-testid': testId,
}) => {
  const {
    about,
    image_url: imageUrl,
    rank,
    rank_color: rankColor,
    score,
    title,
    trend,
  } = l2Score;

  const { data: referenceData } = useGetScoreReference(scoreId);

  return (
    <div className="md:flex flex-col bg-transparent" data-testid={testId}>
      {/* Header Section */}

      <div className="-mt-8 md:mt-12 md:flex md:flex-row-reverse items-center justify-between" data-testid="healthL2Details-headerContent">
        {isLoading ? (
          <Skeleton className="h-52 w-52 rounded-full mb-6 min-w-56" data-testid="healthL2Details-imageSkeleton" />
        ) : (
          <div className="flex items-center justify-center md:justify-end overflow-hidden min-w-60" data-testid="healthL2Details-imageContainer">
            <Image
              src={getL2ImageUrl(imageUrl)}
              alt={title}
              className="h-[13.75rem] w-[13.75rem] md:h-[15.25rem] md:w-[15.25rem] flex items-center justify-center overflow-hidden"
              data-testid="healthL2Details-scoreImage"
            />
          </div>
        )}

        <div className="flex flex-col w-full" data-testid="healthL2Details-scoreInfo">
          <div className="flex lg:flex-col items-center md:items-start justify-between" data-testid="healthL2Details-titleAndScore">
            <div data-testid="healthL2Details-titleSection">
              {isLoading ? (
                <Skeleton className="h-8 w-72 mb-2" data-testid="healthL2Details-titleSkeleton" />
              ) : (
                <h1 className="lg:typography-d1 typography-d2 w-[90%] md:w-full" data-testid="healthL2Details-title">
                  {title}
                </h1>
              )}
              {isLoading ? (
                <Skeleton className="h-5 w-40 mb-6" data-testid="healthL2Details-rankSkeleton" />
              ) : (
                <p
                  className={cn(
                    'typography-body text-left',
                    !isHexColor(rankColor) && rankColor,
                  )}
                  style={
                    isHexColor(rankColor) ? { color: rankColor } : undefined
                  }
                  data-testid="healthL2Details-rank"
                >
                  {rank}
                </p>
              )}
            </div>
            {isLoading ? (
              <Skeleton className="h-12 w-16 mb-4" data-testid="healthL2Details-scoreSkeleton" />
            ) : (
              <h2 className="lg:typography-num-large typography-d1 mb-1 md:mt-8 sm:mt-0" data-testid="healthL2Details-score">
                {score}
              </h2>
            )}
          </div>
          <div className="text-primary w-full" data-testid="healthL2Details-progressSection">
            <div className="w-full typography-footnote text-tertiary mt-3 " data-testid="healthL2Details-progressBar">
              {isLoading ? (
                <Skeleton className="h-2 w-full rounded-full" data-testid="healthL2Details-progressSkeleton" />
              ) : (
                <ProgressBar progress={score} color={rankColor} data-testid="healthL2Details-progress" />
              )}
              <div className="flex items-center justify-between mt-3" data-testid="healthL2Details-progressLabels">
                <p data-testid="healthL2Details-progressMin">0</p>
                <div className="flex items-center justify-between" data-testid="healthL2Details-referenceSection">
                  <HealthReferenceSection scoreId={scoreId} data-testid="healthL2Details-reference" />
                </div>
                <p data-testid="healthL2Details-progressMax">100</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {trend && trend.length > 1 && (
        <div className="mt-12" data-testid="healthL2Details-trendsSection">
          {isLoading ? (
            <Skeleton className="h-32 w-full rounded-lg" data-testid="healthL2Details-trendsSkeleton" />
          ) : (
            <div data-testid="healthL2Details-trendsChart">
              <Trends
                trend={transformTrendToVGraph(trend) || []}
                referenceData={referenceData}
                topIndicator="100"
                bottomIndicator="0"
                data-testid="healthL2Details-trends"
              />
            </div>
          )}
        </div>
      )}

      {/* About Section */}
      <AboutSection
        title={title}
        about={about}
        symptoms={commonlyLinkedSymptoms}
        isLoading={isLoading}
        data-testid="healthL2Details-aboutSection"
      />
    </div>
  );
};
